<template>
  <div :class="rootClasses" :style="rootStyles">
    <ElementControls
      v-if="editing"
      :no-page-break="!pageBreakAllowed"
      @delete="$emit('delete')"
      @duplicate="$emit('duplicate')"
      @page-break="$emit('page-break')"
      @hide="$emit('hide')"
    />
    <div :class="layoutClasses">
      <div
        v-if="label || description"
        class="formester-label-description-wrapper"
        :style="labelDescriptionWrapperStyles"
      >
        <label
          :class="labelClasses"
          :style="labelStyles"
          :for="item.label + item.id"
          v-html="label"
        />
        <SmartText
          :active="active"
          :editing="editing"
          :style="descriptionStyles"
          :text="description"
          :item="item"
        />
      </div>

      <div class="formester-checkbox-options">
        <div
          v-for="option in shuffledOptions"
          :key="option"
          :class="inputClasses"
        >
          <label
            :class="{
              'alternate-checkbox-label': item.isAlternateStyleEnabled,
              'correct-option':
                revealAnswer && correctAnswers.includes(option.id),
              'wrong-option':
                revealAnswer && !correctAnswers.includes(option.id),
              checked:
                item.isAlternateStyleEnabled && itemValue.includes(option.id),
            }"
            :style="propertiesStyles"
          >
            <input
              :id="option.id"
              type="checkbox"
              tabindex="0"
              :checked="itemValue.includes(option.id)"
              :name="`${item.label}-${item.id}`"
              :value="itemValue"
              multiple
              class="custom-checkbox formester-input"
              :class="{
                'altenate-custom-checkbox': item.isAlternateStyleEnabled,
              }"
              :required="required"
              :disabled="
                isEditingDisabled ||
                editing ||
                isSelectionLimitReached(option.id) ||
                isDisabled
              "
              @click="toggleCheck(option.id)"
            />
            <span class="custom-checkmark" />
            <span class="option-label" :style="optionStyles">{{
              option.label
            }}</span>
            <!-- Need to bind this with keyboard event so user can press A,B,C etc. to select options -->
            <!-- <span
              v-if="item.isAlternateStyleEnabled"
              class="alphabetical-count"
              >{{ String.fromCharCode(65 + (index % 26)) }}</span
            > -->
          </label>
        </div>
        <div v-if="item.hasCustomOption" :class="inputClasses">
          <label
            :class="{
              'alternate-checkbox-label': item.isAlternateStyleEnabled,
              checked: item.isAlternateStyleEnabled && isCustomOptionChecked,
            }"
            :style="propertiesStyles"
          >
            <input
              :id="`customOptionInput-${item.id}`"
              :name="`${item.label}-${item.id}`"
              class="custom-checkbox formester-input"
              :class="{
                'altenate-custom-checkbox': item.isAlternateStyleEnabled,
              }"
              type="checkbox"
              :checked="isCustomOptionChecked"
              :disabled="isEditingDisabled || editing || isDisabled"
              @click="handleCustomOptionCheck()"
            />
            <span class="custom-checkmark" />
            <span class="option-label" :style="optionStyles">
              {{ item.customOptionLabel || "Other" }}
            </span>
            <!-- Need to bind this with keyboard event so user can press A,B,C etc. to select options -->
            <!-- <span
              v-if="item.isAlternateStyleEnabled"
              class="alphabetical-count"
              >{{ String.fromCharCode(65 + (item.options.length % 26)) }}</span
            > -->
          </label>
          <div class="mt-2">
            <input
              v-if="isCustomOptionChecked"
              v-model="customOptionValue"
              :placeholder="`Please type ${
                item.customOptionLabel || 'other'
              } value`"
              class="formester-input customOptionInput"
              :class="{
                'alternate-custom-option-input': item.isAlternateStyleEnabled,
              }"
              :style="inputStyles"
            />
          </div>
        </div>
      </div>

      <div
        v-if="showAnswerText"
        class="d-flex align-items-center"
        style="text-decoration: underline"
        :class="{ 'justify-content-end': item.isAlternateStyleEnabled }"
        :style="revealAnswerStyle"
      >
        <span class="pointer" @click="() => (revealAnswer = true)">
          Answer
        </span>
      </div>

      <div
        v-if="revealAnswer && !item.isAlternateStyleEnabled"
        :style="revealAnswerStyle"
      >
        <b>Correct answer:</b> {{ correctAnswerLabels.join(", ") }}
      </div>

      <ErrorMessage :item="item" />

      <QuizFeedback
        v-if="revealAnswer"
        class="mt-3"
        :type="quizFeedbackType"
        :message="quizFeedbackMessage"
      />
    </div>
  </div>
</template>

<script>
import ElementControls from "@/components/form_fields/shared/element-controls.vue";
import commonStyles from "@/components/form_fields/shared/commonStyles";
import sharedActions from "@/components/form_fields/shared/sharedActions";
import ErrorMessage from "@/components/form_fields/shared/error-message.vue";
import { set as setItem } from "lodash-es";
import SmartText from "@/components/form_fields/shared/smart-text.vue";
import QuizFeedback from "@/components/form_fields/shared/_quiz-feedback.vue";
import { set } from "lodash";

export default {
  name: "MultipleCheckbox",
  components: {
    ElementControls,
    ErrorMessage,
    SmartText,
    QuizFeedback,
  },
  mixins: [commonStyles, sharedActions],
  props: {
    item: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    styling: {
      type: Object,
      required: true,
    },
    pageBreakAllowed: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["delete", "page-break", "duplicate", "hide"],
  computed: {
    required() {
      if (!this.item.required) {
        return false;
      }

      if (this.isCustomOptionChecked) {
        return !this.customOptionValue;
      }

      return this.itemValue && this.itemValue.length === 0;
    },
    itemValue: {
      get() {
        return this.item.value;
      },
      set(e) {
        setItem(this.item, "value", e);
      },
    },
    isCustomOptionChecked: {
      get() {
        return this.item.isCustomOptionChecked;
      },
      set(e) {
        setItem(this.item, "isCustomOptionChecked", e);
      },
    },
    customOptionValue: {
      get() {
        return this.item.customOptionValue;
      },
      set(e) {
        setItem(this.item, "customOptionValue", e);
      },
    },
    inputClasses() {
      return {
        // 'formester-input': true,
        "formester-input__multiple-checkbox": true,
        "checkbox-option": true,
      };
    },
    areAllValuesCorrect() {
      return (
        this.itemValue.length > 0 &&
        this.itemValue.every((value) => this.correctAnswers.includes(value))
      );
    },
    quizFeedbackType() {
      return this.areAllValuesCorrect ? "success" : "danger";
    },
    quizFeedbackMessage() {
      if (this.areAllValuesCorrect) {
        return this.item.correctAnswerFeedback || "Yup, Correct answer";
      }

      return this.item.wrongAnswerFeedback || "Opps! Wrong";
    },
    propertiesStyles() {
      return {
        cursor: this.isDisabled && !this.editing ? "not-allowed" : "auto",
        opacity: this.isDisabled && !this.editing ? 0.6 : 1,
      };
    },
  },
  mounted() {
    if (this.itemValue && typeof this.itemValue === "string") {
      this.itemValue = [this.itemValue];
    }
  },
  methods: {
    toggleCheck(option) {
      if (this.isEditingDisabled) return;

      if (this.itemValue.includes(option)) {
        this.itemValue = this.itemValue.filter((op) => op != option);
      } else {
        this.itemValue = [...this.itemValue, option];
      }
    },
    handleCustomOptionCheck() {
      this.isCustomOptionChecked = !this.isCustomOptionChecked;
    },
    isSelectionLimitReached(option) {
      return (
        this.item.hasSelectionLimit &&
        !this.itemValue.includes(option) &&
        this.itemValue.length >= this.item.selectionLimit
      );
    },
  },
};
</script>

<style scoped>
/* Few styles for this component is added in formester-form.css */
.alphabetical-count {
  color: var(--font-color-eighty-opacity);
  cursor: default;
}
</style>
